import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useEffect, useMemo, useState, type ComponentProps } from "react";
import type { BookingOffering } from "./api.js";
import { idempotencyKey } from "./api.js";
import { uncertainBookingMessageFor } from "./attempt.js";
import { asLocalInput, displacementTo, shiftByNaive } from "./reschedule.js";
import { staffBooking, type StaffLessonBookingResult } from "./staff.js";
import {
  staffWorkingHours,
  type StaffSlotCheck,
} from "./staffWorkingHours.js";
import {
  defaultFundingMode,
  fundingModes,
  offeringKey,
  type TutorFundingMode,
} from "./tutorBookingFunding.js";
import {
  tutorBookingAttemptAfterFailure,
  tutorBookingOutcome,
  tutorOfferingFailure,
  TutorBookLesson as TutorBookLessonCore,
} from "./TutorBookLessonCore.js";
import { tutorOneOffBooking } from "./tutorOneOffBooking.js";
import { BookingConfirmationEmailControl } from "./BookingConfirmationEmailControl.js";

type HistoricalLessonProps = ComponentProps<typeof TutorBookLessonCore>;

function historicalInstant(localValue: string): string | null {
  const seed = new Date().toISOString();
  const displacement = displacementTo(seed, localValue);
  if (displacement === null) return null;
  return shiftByNaive(seed, displacement);
}

function isPast(iso: string | null): boolean {
  if (iso === null) return false;
  const parsed = Date.parse(iso);
  return Number.isFinite(parsed) && parsed < Date.now();
}

export function HistoricalLessonBooking({
  studentId,
  studentName = null,
  selectedTutorProviderId = null,
  initialStartsAt = null,
  onBooked,
  onMutationBusyChange,
  appointmentMode = false,
}: HistoricalLessonProps) {
  const publish = usePublishActionOutcome();
  const [hosted, setHosted] = useState(false);
  const [offerings, setOfferings] = useState<BookingOffering[] | null>(null);
  const [selectedOfferingKey, setSelectedOfferingKey] = useState<string | null>(
    null,
  );
  const [fundingMode, setFundingMode] = useState<TutorFundingMode | null>(null);
  const [localStart, setLocalStart] = useState(() =>
    initialStartsAt !== null && isPast(initialStartsAt)
      ? asLocalInput(initialStartsAt)
      : "",
  );
  const [checked, setChecked] = useState<{
    startsAt: string;
    check: StaffSlotCheck;
  } | null>(null);
  const [busy, setBusy] = useState<
    "offerings" | "checking" | "booking" | null
  >("offerings");
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<ActionOutcome | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [key, setKey] = useState(idempotencyKey);
  const [notifyParticipants, setNotifyParticipants] = useState(false);

  useEffect(() => {
    let active = true;
    setBusy("offerings");
    setProblem(null);
    setOfferings(null);
    setChecked(null);
    setNotifyParticipants(false);
    void tutorOneOffBooking.offerings(studentId, selectedTutorProviderId).then(
      (result) => {
        if (!active) return;
        const scoped = result.offerings.filter(
          (offering) =>
            (selectedTutorProviderId === null ||
              offering.provider_id === selectedTutorProviderId) &&
            fundingModes(offering).length > 0,
        );

        // A remembered preference describes future booking behaviour. When
        // recording a past lesson, only a genuinely unambiguous single service
        // is safe to preselect; otherwise the tutor must choose what happened.
        const initial = scoped.length === 1 ? scoped[0]! : null;
        setOfferings(scoped);
        setSelectedOfferingKey(
          initial === null ? null : offeringKey(initial),
        );
        setFundingMode(
          initial === null ? null : defaultFundingMode(initial),
        );
        setBusy(null);
      },
      (cause: unknown) => {
        if (!active) return;
        setProblem(tutorOfferingFailure(cause));
        setBusy(null);
      },
    );
    return () => {
      active = false;
    };
  }, [selectedTutorProviderId, studentId]);

  useEffect(() => {
    onMutationBusyChange?.(busy === "booking");
    return () => onMutationBusyChange?.(false);
  }, [busy, onMutationBusyChange]);

  const selectedOffering = useMemo(
    () =>
      offerings?.find(
        (offering) => offeringKey(offering) === selectedOfferingKey,
      ) ?? null,
    [offerings, selectedOfferingKey],
  );
  const selectedModes =
    selectedOffering === null ? [] : fundingModes(selectedOffering);
  const candidate = historicalInstant(localStart);
  const candidateIsPast = isPast(candidate);
  const checkedCurrent = checked !== null && checked.startsAt === candidate;

  function resetCheck() {
    setChecked(null);
    setProblem(null);
    setKey(idempotencyKey());
  }

  function chooseOffering(nextKey: string) {
    const next =
      offerings?.find((offering) => offeringKey(offering) === nextKey) ?? null;
    setSelectedOfferingKey(next === null ? null : nextKey);
    setFundingMode(next === null ? null : defaultFundingMode(next));
    resetCheck();
  }

  async function checkTime() {
    if (selectedOffering === null || candidate === null) {
      setProblem("Choose a valid past date and time first.");
      return;
    }
    if (!candidateIsPast) {
      setProblem(
        "Historical booking is only for a time that has already passed. Use Upcoming for a future lesson.",
      );
      setChecked(null);
      return;
    }

    setBusy("checking");
    setProblem(null);
    setChecked(null);
    try {
      const exact = await staffWorkingHours.check({
        providerId: selectedOffering.provider_id,
        serviceId: selectedOffering.service_id,
        startsAt: candidate,
      });
      if (
        exact.state !== "historical" ||
        !(exact.schedule_override_available ?? exact.working_hours_override_available)
      ) {
        setProblem(
          exact.message ??
            "That historical time conflicts with another diary entry and cannot be overridden.",
        );
        return;
      }
      setChecked({ startsAt: candidate, check: exact });
    } catch (cause) {
      setProblem(
        cause instanceof Error
          ? cause.message
          : "Could not check that historical lesson time.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function confirm() {
    if (
      selectedOffering === null ||
      fundingMode === null ||
      checked === null ||
      checked.startsAt !== candidate ||
      !candidateIsPast
    ) {
      return;
    }

    setBusy("booking");
    setProblem(null);
    try {
      const input = {
        provider_id: selectedOffering.provider_id,
        service_id: selectedOffering.service_id,
        starts_at: checked.startsAt,
        notify_participants: notifyParticipants,
      };
      const result: StaffLessonBookingResult =
        fundingMode === "lesson_credit"
          ? await staffBooking.creditFundedBooking(studentId, input, key, true)
          : await tutorOneOffBooking.paymentPendingBooking(
              studentId,
              input,
              key,
              true,
            );
      const receipt = tutorBookingOutcome(result, studentName, selectedOffering.service_name);
      setDone(receipt);
      setHosted(publish(receipt));
      onBooked();
    } catch (cause) {
      const attempt = tutorBookingAttemptAfterFailure(key, cause);
      setProblem(
        attempt.kind === "uncertain"
          ? uncertainBookingMessageFor(cause)
          : attempt.message,
      );
      setKey(attempt.key);
      setUncertain(attempt.kind === "uncertain");
      if (attempt.kind === "uncertain") setHosted(publish({ variant: "uncertain", title: "Booking outcome needs checking", description: studentName, details: [{ value: uncertainBookingMessageFor(cause) }] }));
      if (attempt.kind === "rejected") setChecked(null);
    } finally {
      setBusy(null);
    }
  }

  if (done !== null && hosted) return null;
  if (done !== null) {
    return (
      <div className="panel">
        <ActionConfirmation {...done} reveal />
      </div>
    );
  }

  return (
    <div className="panel historical-lesson-booking">
      <h3 style={{ marginTop: 0 }}>Book a past lesson</h3>
      <div className="notice">
        <strong>This creates a historical lesson.</strong>
        <p className="small" style={{ marginBottom: 0 }}>
          Use this only for a lesson that already happened. The booking will be
          written to the diary and may consume a lesson credit or create payment
          due, exactly as selected below. Existing diary clashes are still
          blocked.
        </p>
      </div>

      {offerings === null ? (
        <p className="empty">
          {busy === "offerings"
            ? "Loading lesson options…"
            : "Lesson options are unavailable."}
        </p>
      ) : offerings.length === 0 ? (
        <p className="empty">
          No lesson service is available in this student and tutor scope.
        </p>
      ) : (
        <>
          <label className="stack" style={{ marginTop: 12 }}>
            <span className="small muted">Lesson type</span>
            <select
              aria-label="Historical lesson type"
              value={selectedOfferingKey ?? ""}
              disabled={busy !== null}
              onChange={(event) => chooseOffering(event.target.value)}
            >
              <option value="">Choose a lesson type</option>
              {offerings.map((offering) => {
                const nextKey = offeringKey(offering);
                return (
                  <option key={nextKey} value={nextKey}>
                    {offering.service_name ??
                      `Lesson service ${offering.service_id}`}
                  </option>
                );
              })}
            </select>
          </label>

          {selectedOffering !== null && selectedModes.length > 1 && (
            <div
              className="row"
              style={{ marginTop: 10, flexWrap: "wrap" }}
              role="group"
              aria-label="Historical lesson funding"
            >
              <button
                type="button"
                className={fundingMode === "lesson_credit" ? "" : "quiet"}
                disabled={busy !== null}
                onClick={() => {
                  setFundingMode("lesson_credit");
                  setKey(idempotencyKey());
                  setProblem(null);
                }}
              >
                Use lesson credit
              </button>
              <button
                type="button"
                className={fundingMode === "payment_pending" ? "" : "quiet"}
                disabled={busy !== null}
                onClick={() => {
                  setFundingMode("payment_pending");
                  setKey(idempotencyKey());
                  setProblem(null);
                }}
              >
                Payment due
              </button>
            </div>
          )}

          <div
            className="row"
            style={{ alignItems: "flex-end", marginTop: 12, flexWrap: "wrap" }}
          >
            <label className="stack">
              <span className="small muted">Past date and time</span>
              <input
                type="datetime-local"
                value={localStart}
                disabled={busy !== null}
                onChange={(event) => {
                  setLocalStart(event.target.value);
                  resetCheck();
                }}
              />
            </label>
            <button
              type="button"
              className="action-secondary"
              disabled={
                busy !== null || selectedOffering === null || localStart === ""
              }
              onClick={() => void checkTime()}
            >
              {busy === "checking" ? "Checking time…" : "Check past time"}
            </button>
          </div>

          {candidate !== null && !candidateIsPast && localStart !== "" && (
            <p className="small muted">
              That time has not passed yet. Use Upcoming to book it.
            </p>
          )}

          {checkedCurrent && selectedOffering !== null && (
            <div className="notice" style={{ marginTop: 12 }}>
              <strong>Past-time warning confirmed.</strong>
              <p className="small">
                {studentName ? `${studentName} · ` : ""}
                {selectedOffering.service_name ??
                  `Lesson service ${selectedOffering.service_id}`}
                . This historical time is free of diary clashes, but it will be
                recorded after the fact.
              </p>
              <p className="small">
                {fundingMode === "lesson_credit"
                  ? "1 lesson credit will be used."
                  : "Payment will remain due and can be marked as received later."}
              </p>
              <BookingConfirmationEmailControl
                checked={notifyParticipants}
                disabled={busy === "booking" || uncertain}
                onChange={setNotifyParticipants}
              />
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => void confirm()}
              >
                {busy === "booking"
                  ? "Booking past lesson…"
                  : "Book past lesson"}
              </button>
            </div>
          )}
        </>
      )}

      {problem && (
        <>{uncertain ? !hosted && <ActionConfirmation variant="uncertain" title="Booking outcome needs checking" description={problem} reveal /> : <div className="notice error" role="alert">{problem}</div>}</>
      )}
    </div>
  );
}
